import {
  BulkManpowerAction,
  ManpowerAdjustment,
  ManpowerComment,
  ManpowerCompany,
  ManpowerEntry,
  ManpowerFormData,
  ManpowerPermissions,
  ManpowerQuery,
  ManpowerStats,
  PayrollSummary,
  Project,
} from '../types';
import api from './api';

class ManpowerService {
  // Get manpower entries with filtering
  async getEntries(query: ManpowerQuery = {}) {
    try {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await api.get(`/manpower/entries?${params.toString()}`);
      return {
        success: true,
        data: {
          entries: response.data.entries as ManpowerEntry[],
          total: response.data.total,
          page: response.data.page,
          totalPages: response.data.totalPages,
        },
      };
    } catch (error: any) {
      console.error('Failed to fetch manpower entries:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch manpower entries',
      };
    }
  }

  // Get manpower entries for specific project
  async getManpowerEntries(projectId: string, query: any = {}) {
    try {
      const params = new URLSearchParams();

      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await api.get(`/projects/${projectId}/manpower?${params.toString()}`);
      return {
        success: true,
        data: {
          entries: response.data.entries,
          total: response.data.total,
          page: response.data.page,
          totalPages: response.data.totalPages,
        },
      };
    } catch (error: any) {
      console.error('Failed to fetch manpower entries:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch manpower entries',
      };
    }
  }

  // Get single manpower entry
  async getEntry(entryId: string) {
    try {
      const response = await api.get(`/manpower/entries/${entryId}`);
      return response.data as ManpowerEntry;
    } catch (error) {
      console.error('Failed to fetch manpower entry:', error);
      throw error;
    }
  }

  // Create new manpower entry (supports both single entry and bulk entries)
  async createEntry(formData: ManpowerFormData, onProgress?: (progress: number) => void) {
    try {
      // Validate project_id is provided
      if (!formData.project_id) {
        throw new Error('Project ID is required');
      }

      // Handle new bulk format with entries array
      if (formData.entries && formData.entries.length > 0) {
        // Prepare complete request body with all required fields
        const requestBody = {
          entries: formData.entries.map(entry => ({
            entry_type: entry.entry_type,
            worker_id: entry.worker_id || '',
            worker_name: entry.worker_name || '',
            // For individual workers, use the worker_id as company_id, otherwise use the actual company_id
            company_id:
              entry.entry_type === 'individual' ? entry.worker_id || '' : entry.company_id || '',
            company_name:
              entry.company_name || (entry.entry_type === 'individual' ? 'Individual Worker' : ''),
            number_of_workers: entry.number_of_workers || 1,
            role: entry.role || 'General Worker',
            hours_worked: entry.hours_worked || 0,
            overtime_hours: entry.overtime_hours || 0,
            hourly_rate: entry.hourly_rate || 0,
            overtime_rate: entry.overtime_rate || 0,
            comments: entry.comments || '',
          })),
          location: {
            site: formData.location?.site || 'Main Site',
            building: formData.location?.building || '',
            floor: formData.location?.floor || '',
            gps: formData.location?.gps || '',
          },
          date: formData.date,
          // Add required supervisor information
          supervisor: {
            name: formData.supervisorName || 'Site Supervisor',
            contact: formData.supervisorContact || 'N/A',
          },
          // Add required shift information
          shift: {
            type: formData.shiftType || 'day',
            startTime: formData.startTime || '08:00',
            endTime: formData.endTime || '17:00',
          },
          // Add required environmental factors
          environmentalFactors: {
            weather: formData.weatherCondition || 'clear',
          },
          attachments: formData.attachmentIds || [], // Image reference IDs
        };

        const response = await api.post(`/projects/${formData.project_id}/manpower`, requestBody, {
          headers: {
            'Content-Type': 'application/json',
          },
          onUploadProgress: progressEvent => {
            if (progressEvent.total && onProgress) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(percentCompleted);
            }
          },
        });

        return {
          success: true,
          data: response.data,
          message: `Successfully created ${formData.entries.length} entries`,
        };
      } else {
        // Handle legacy single entry format for backward compatibility
        const requestBody = {
          entry_type: (formData as any).entry_type,
          worker_id: (formData as any).worker_id || '',
          worker_name: (formData as any).worker_name || '',
          company_id:
            (formData as any).company_id ||
            ((formData as any).entry_type === 'individual' ? 'individual' : ''),
          company_name:
            (formData as any).company_name ||
            ((formData as any).entry_type === 'individual' ? 'Individual Worker' : ''),
          number_of_workers: (formData as any).number_of_workers || 1,
          role: (formData as any).role || 'General Worker',
          location: {
            site: formData.location?.site || 'Main Site',
            building: formData.location?.building || '',
            floor: formData.location?.floor || '',
            gps: formData.location?.gps || '',
          },
          date: formData.date,
          hours_worked: (formData as any).hours_worked || 0,
          overtime_hours: (formData as any).overtime_hours || 0,
          hourly_rate: (formData as any).hourly_rate || 0,
          overtime_rate: (formData as any).overtime_rate || 0,
          comments: (formData as any).comments || '',
          attachments: formData.attachmentIds || [], // Image reference IDs
        };

        const response = await api.post(`/projects/${formData.project_id}/manpower`, requestBody, {
          headers: {
            'Content-Type': 'application/json',
          },
          onUploadProgress: progressEvent => {
            if (progressEvent.total && onProgress) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(percentCompleted);
            }
          },
        });

        return {
          success: true,
          data: response.data,
          message: 'Entry created successfully',
        };
      }
    } catch (error) {
      console.error('Failed to create manpower entry:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to create entry',
      };
    }
  }

  // Update manpower entry
  async updateEntry(projectId: string, entryId: string, updates: Partial<ManpowerEntry>) {
    try {
      const response = await api.put(`/projects/${projectId}/manpower/${entryId}`, updates);
      return {
        success: true,
        data: response.data,
        message: 'Entry updated successfully',
      };
    } catch (error: any) {
      console.error('Failed to update manpower entry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update entry',
      };
    }
  }

  // Delete manpower entry
  async deleteEntry(projectId: string, entryId: string) {
    try {
      const response = await api.delete(`/projects/${projectId}/manpower/${entryId}`);
      return {
        success: true,
        data: response.data,
        message: 'Entry deleted successfully',
      };
    } catch (error: any) {
      console.error('Failed to delete manpower entry:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete entry',
      };
    }
  }

  // Get manpower statistics
  async getManpowerStats(projectId: string) {
    try {
      const response = await api.get(`/projects/${projectId}/manpower/stats`);
      return {
        success: true,
        data: response.data as ManpowerStats,
      };
    } catch (error: any) {
      console.error('Failed to fetch manpower stats:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch manpower stats',
      };
    }
  }

  // Get manpower companies
  async getManpowerCompanies(search?: string) {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('activeOnly', 'true');

      const response = await api.get(`/manpower/companies?${params.toString()}`);
      return {
        success: true,
        data: response.data.companies as ManpowerCompany[],
      };
    } catch (error: any) {
      console.error('Failed to fetch manpower companies:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch companies',
        data: [],
      };
    }
  }

  // Get companies for dropdown
  async getCompaniesForDropdown(search?: string) {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      // Try the dropdown endpoint first, fallback to regular companies endpoint
      let response;
      try {
        response = await api.get(`/manpower/companies/dropdown?${params.toString()}`);

        // Check if response has success flag and data
        const dropdownData = response.data.success ? response.data.data : response.data;

        return (Array.isArray(dropdownData) ? dropdownData : []).map((company: any) => ({
          value: company.id || company._id,
          label: company.label || company.name,
          description: company.contactPerson ? `Contact: ${company.contactPerson}` : undefined,
          metadata: {
            contactPerson: company.contactPerson,
            rating: company.rating,
            phone: company.phone,
          },
        }));
      } catch (dropdownError) {
        console.log('Dropdown endpoint failed, trying regular endpoint:', dropdownError);

        // Fallback to regular companies endpoint
        response = await api.get(`/manpower/companies?${params.toString()}`);

        // Handle API response structure
        const responseData = response.data.success ? response.data.data : response.data;
        const companiesList = responseData.companies || responseData;

        // Filter for actual companies (exclude individual workers)
        const companies = (Array.isArray(companiesList) ? companiesList : []).filter(
          (company: any) =>
            company.isWorker !== true &&
            !company.notes?.includes('Individual Worker') &&
            company.name !== company.contactPerson // Heuristic to exclude individual workers
        );

        return companies.map((company: any) => ({
          value: company._id,
          label: company.name,
          description: company.contactPerson ? `Contact: ${company.contactPerson}` : undefined,
          metadata: {
            contactPerson: company.contactPerson,
            rating: company.rating || 0,
            phone: company.phone,
            email: company.email,
            license: company.license,
          },
        }));
      }
    } catch (error) {
      console.error('Failed to fetch companies for dropdown:', error);
      return [];
    }
  }

  // Get workers for dropdown (filtered from companies marked as workers)
  async getWorkersForDropdown(search?: string) {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      // Get all companies and filter for workers on frontend
      const response = await api.get(`/manpower/companies?${params.toString()}`);

      // Handle API response structure
      const responseData = response.data.success ? response.data.data : response.data;
      const companiesList = responseData.companies || responseData;

      // Filter for individual workers (companies marked with isWorker flag or single-person companies)
      const workers = (Array.isArray(companiesList) ? companiesList : []).filter(
        (company: any) =>
          company.isWorker === true ||
          company.notes?.includes('Individual Worker') ||
          (company.name === company.contactPerson && company.name) // Heuristic for individual workers
      );

      return workers.map((worker: any) => ({
        value: worker._id,
        label: worker.name,
        description: worker.specializations?.[0]
          ? `${worker.specializations[0]}`
          : worker.contactPerson,
        metadata: {
          role: worker.workerDetails?.role || 'Worker',
          specialization: worker.specializations?.[0] || '',
          hourlyRate: worker.workerDetails?.hourlyRate || 0,
          phone: worker.phone,
          email: worker.email,
        },
      }));
    } catch (error) {
      console.error('Failed to fetch workers for dropdown:', error);
      return [];
    }
  }

  // Create new worker (using company endpoint with worker flag)
  async createWorker(workerData: any) {
    try {
      // Transform worker data to company format for API
      const companyData = {
        name: workerData.name,
        contactPerson: workerData.name,
        phone: workerData.phone || '',
        email: workerData.email || '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
        },
        specializations: [workerData.specialization || ''],
        contractTerms: {
          paymentTerms: 'net_30',
          preferredCurrency: 'USD',
          overtimeRate: workerData.hourlyRate ? workerData.hourlyRate * 1.5 : 0,
        },
        notes: `Individual Worker - ${workerData.role || ''}\n${workerData.notes || ''}`,
        isWorker: true, // Flag to identify as individual worker
        workerDetails: {
          role: workerData.role,
          specialization: workerData.specialization,
          hourlyRate: workerData.hourlyRate,
          emergencyContact: workerData.emergencyContact,
        },
      };

      const response = await api.post('/manpower/companies', companyData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to create worker:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create worker',
      };
    }
  }

  // Create new company
  async createCompany(companyData: any) {
    try {
      // Transform frontend company data to backend format
      const backendCompanyData = {
        name: companyData.name,
        contactPerson: companyData.contactPerson,
        phone: companyData.contactPhone || companyData.phone || '',
        email: companyData.contactEmail || companyData.email || '',
        address: {
          street: companyData.address || '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
        },
        license: companyData.registrationNumber || '',
        specializations: [companyData.companyType || ''],
        contractTerms: {
          paymentTerms: 'net_30',
          preferredCurrency: 'USD',
          overtimeRate: companyData.defaultHourlyRate ? companyData.defaultHourlyRate * 1.5 : 0,
        },
        taxInfo: {
          taxId: companyData.taxNumber || '',
          businessType: 'corporation',
        },
        notes: companyData.notes || '',
        isWorker: false, // Flag to identify as company
      };

      const response = await api.post('/manpower/companies', backendCompanyData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Failed to create company:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create company',
      };
    }
  }

  // Get projects for dropdown
  async getProjectsForDropdown() {
    try {
      const response = await api.get('/projects');
      return response.data.projects.map((project: Project) => ({
        value: project.id,
        label: project.name,
        metadata: {
          status: project.status,
          priority: project.priority,
        },
      }));
    } catch (error) {
      console.error('Failed to fetch projects for dropdown:', error);
      return [];
    }
  }

  // Get worker roles from configuration
  async getWorkerRoles() {
    try {
      const response = await api.get('/configurations/worker-roles/options');
      return response.data.map((role: any) => ({
        value: role.value,
        label: role.label,
        metadata: role.metadata,
      }));
    } catch (error) {
      console.error('Failed to fetch worker roles:', error);
      // Fallback to static roles if API fails
      return [
        { value: 'skilled', label: 'Skilled Worker' },
        { value: 'semi_skilled', label: 'Semi-Skilled Worker' },
        { value: 'unskilled', label: 'Unskilled Worker' },
        { value: 'supervisor', label: 'Supervisor' },
        { value: 'foreman', label: 'Foreman' },
        { value: 'engineer', label: 'Engineer' },
        { value: 'safety_officer', label: 'Safety Officer' },
        { value: 'operator', label: 'Equipment Operator' },
      ];
    }
  }

  // Get worker types from configuration
  async getWorkerTypes() {
    try {
      const response = await api.get('/configurations/worker-types/options');
      return response.data.map((type: any) => ({
        value: type.value,
        label: type.label,
        metadata: type.metadata,
      }));
    } catch (error) {
      console.error('Failed to fetch worker types:', error);
      // Fallback to static types if API fails
      return [
        { value: 'electrician', label: 'Electrician' },
        { value: 'plumber', label: 'Plumber' },
        { value: 'carpenter', label: 'Carpenter' },
        { value: 'mason', label: 'Mason' },
        { value: 'painter', label: 'Painter' },
        { value: 'welder', label: 'Welder' },
        { value: 'hvac', label: 'HVAC Technician' },
        { value: 'general_labor', label: 'General Labor' },
      ];
    }
  }

  // Get shift types from configuration
  async getShiftTypes() {
    try {
      const response = await api.get('/configurations/shift-types/options');
      return response.data.map((shift: any) => ({
        value: shift.value,
        label: shift.label,
        metadata: shift.metadata,
      }));
    } catch (error) {
      console.error('Failed to fetch shift types:', error);
      // Fallback to static types if API fails
      return [
        { value: 'day', label: 'Day Shift' },
        { value: 'night', label: 'Night Shift' },
        { value: 'overtime', label: 'Overtime' },
        { value: 'double_shift', label: 'Double Shift' },
      ];
    }
  }

  // Get status options from configuration
  async getStatusOptions() {
    try {
      const response = await api.get('/configurations/manpower-status/options');
      return response.data.map((status: any) => ({
        value: status.value,
        label: status.label,
        metadata: status.metadata,
      }));
    } catch (error) {
      console.error('Failed to fetch status options:', error);
      // Fallback to static statuses if API fails
      return [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'cancelled', label: 'Cancelled' },
      ];
    }
  }

  // Add adjustment to entry
  async addAdjustment(
    entryId: string,
    adjustment: Omit<ManpowerAdjustment, 'adjustment_id' | 'added_by' | 'added_at'>
  ) {
    try {
      const response = await api.post(`/manpower/entries/${entryId}/adjustments`, adjustment);
      return response.data as ManpowerEntry;
    } catch (error) {
      console.error('Failed to add adjustment:', error);
      throw error;
    }
  }

  // Add comment to entry
  async addComment(
    entryId: string,
    comment: Omit<ManpowerComment, 'comment_id' | 'added_by' | 'added_at'>
  ) {
    try {
      const response = await api.post(`/manpower/entries/${entryId}/comments`, comment);
      return response.data as ManpowerEntry;
    } catch (error) {
      console.error('Failed to add comment:', error);
      throw error;
    }
  }

  // Approve entry
  async approveEntry(entryId: string, notes?: string) {
    try {
      const response = await api.post(`/manpower/entries/${entryId}/approve`, { notes });
      return response.data as ManpowerEntry;
    } catch (error) {
      console.error('Failed to approve entry:', error);
      throw error;
    }
  }

  // Mark entry as paid
  async markAsPaid(entryId: string, paymentNotes?: string) {
    try {
      const response = await api.post(`/manpower/entries/${entryId}/pay`, {
        payment_notes: paymentNotes,
      });
      return response.data as ManpowerEntry;
    } catch (error) {
      console.error('Failed to mark entry as paid:', error);
      throw error;
    }
  }

  // Bulk actions
  async bulkAction(action: BulkManpowerAction) {
    try {
      const response = await api.post('/manpower/entries/bulk', action);
      return response.data;
    } catch (error) {
      console.error('Failed to perform bulk action:', error);
      throw error;
    }
  }

  // Get payroll summary
  async getPayrollSummary(month: number, year: number, projectId?: string) {
    try {
      const params = new URLSearchParams({
        month: month.toString(),
        year: year.toString(),
      });

      if (projectId) {
        params.append('project_id', projectId);
      }

      const response = await api.get(`/manpower/payroll?${params.toString()}`);
      return response.data as PayrollSummary;
    } catch (error) {
      console.error('Failed to fetch payroll summary:', error);
      throw error;
    }
  }

  // Get manpower statistics
  async getStats(projectId?: string) {
    try {
      const params = projectId ? `?project_id=${projectId}` : '';
      const response = await api.get(`/manpower/stats${params}`);
      return response.data as ManpowerStats;
    } catch (error) {
      console.error('Failed to fetch manpower stats:', error);
      throw error;
    }
  }

  // Export payroll data
  async exportPayroll(
    month: number,
    year: number,
    projectId?: string,
    format: 'csv' | 'pdf' = 'csv'
  ) {
    try {
      const params = new URLSearchParams({
        month: month.toString(),
        year: year.toString(),
        format,
      });

      if (projectId) {
        params.append('project_id', projectId);
      }

      const response = await api.get(`/manpower/payroll/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const filename = `payroll-${year}-${month.toString().padStart(2, '0')}.${format}`;
      this.triggerDownload(response.data, filename);
    } catch (error) {
      console.error('Failed to export payroll:', error);
      throw error;
    }
  }

  // Get user permissions
  async getPermissions() {
    try {
      const response = await api.get('/manpower/permissions');
      return response.data as ManpowerPermissions;
    } catch (error) {
      console.error('Failed to fetch manpower permissions:', error);
      // Return default permissions if API fails
      return {
        view: false,
        log: false,
        approve: false,
        pay: false,
      };
    }
  }

  // Wage calculation utilities
  calculateWages(
    entry: Pick<
      ManpowerEntry,
      | 'number_of_workers'
      | 'hours_worked'
      | 'overtime_hours'
      | 'hourly_rate'
      | 'overtime_rate'
      | 'adjustments'
    >
  ) {
    const {
      number_of_workers = 0,
      hours_worked = 0,
      overtime_hours = 0,
      hourly_rate = 0,
      overtime_rate = 0,
      adjustments = [],
    } = entry;

    // Base calculations
    const regularPay = number_of_workers * hours_worked * hourly_rate;
    const overtimePay = number_of_workers * overtime_hours * overtime_rate;
    const basePay = regularPay + overtimePay;

    // Apply adjustments
    const totalAdjustments = adjustments.reduce((sum, adj) => sum + (adj.amount || 0), 0);
    const totalPay = basePay + totalAdjustments;

    return {
      regularPay,
      overtimePay,
      basePay,
      totalAdjustments,
      totalPay: Math.max(0, totalPay), // Ensure non-negative
    };
  }

  // Real-time wage calculation for forms
  calculateFormWages(formData: Partial<ManpowerFormData>, adjustments: ManpowerAdjustment[] = []) {
    const numberOfWorkers = formData.number_of_workers || 0;
    const hoursWorked = formData.hours_worked || 0;
    const hourlyRate = formData.hourly_rate || 0;

    if (!numberOfWorkers || !hoursWorked || !hourlyRate) {
      return {
        regularPay: 0,
        overtimePay: 0,
        basePay: 0,
        totalAdjustments: 0,
        totalPay: 0,
      };
    }

    return this.calculateWages({
      number_of_workers: numberOfWorkers,
      hours_worked: hoursWorked,
      overtime_hours: formData.overtime_hours || 0,
      hourly_rate: hourlyRate,
      overtime_rate: formData.overtime_rate || hourlyRate * 1.5,
      adjustments,
    });
  }

  // Utility function to format currency
  formatCurrency(amount: number, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  // Utility function to format hours
  formatHours(hours: number) {
    if (hours === 0) return '0h';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
  }

  // Get status color for UI
  getStatusColor(status: string) {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-red-100 text-red-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  // Helper method to trigger file download
  private triggerDownload(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default new ManpowerService();
